import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }> | string | React.ReactNode;
  description?: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
  };

  const renderIcon = (icon: any) => {
    if (!icon) return null;
    if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent className="w-4 h-4 text-gray-500 dark:text-gray-450 shrink-0" />;
    }
    if (typeof icon === 'string') {
      return <span className="text-base shrink-0 select-none">{icon}</span>;
    }
    return <span className="shrink-0">{icon}</span>;
  };

  return (
    <div ref={containerRef} className={`relative select-none ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-white dark:bg-[#212121] border border-gray-200/90 dark:border-gray-800 rounded-xl px-4 py-2.5 text-sm text-gray-800 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-700 hover:shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-black/5 dark:focus:ring-white/5 cursor-pointer text-left"
      >
        <div className="flex items-center gap-2.5 truncate">
          {renderIcon(selectedOption?.icon)}
          <span className="truncate font-medium">{selectedOption?.label}</span>
        </div>
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0 ml-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>

      {/* Options List */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="absolute z-50 mt-1.5 w-full bg-white dark:bg-[#212121] border border-gray-200/60 dark:border-gray-850 rounded-xl shadow-xl py-1.5 max-h-60 overflow-y-auto no-scrollbar focus:outline-none origin-top"
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`w-full flex items-center justify-between px-3.5 py-2 text-sm transition-all duration-150 cursor-pointer text-left hover:bg-gray-50/80 dark:hover:bg-[#171717]/80 ${
                    isSelected ? 'bg-gray-50/50 dark:bg-[#171717]/50 text-black dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    {renderIcon(option.icon)}
                    <div className="flex flex-col truncate">
                      <span className="truncate">{option.label}</span>
                      {option.description && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-normal mt-0.5 truncate">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <svg
                      className="w-4 h-4 text-black dark:text-white shrink-0 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
